import React, { useState } from 'react'
import { Avatar, Card, Grid, IconButton, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../../../services/api'
import { getTitleByType, setLimitCharacter } from '../../../../utils'
import Iconify from '../../../../components/Iconify'
import { DialogConfirmation } from '../../../../components/DialogConfirmation'
import { SequenceTypeIcon } from '../../../TrainingBench/components/SequenceTypeIcon'
import useResponsive from 'hooks/useResponsive'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { ReorderDaysOfWeek } from 'components/ReorderDaysOfWeek'
import { AthleteTraining } from 'models/interfaces/athlete-training.interface'

type Props = {
  items: AthleteTraining[]
  onEdit: any
  onAthleteTrainingList: (data: AthleteTraining[]) => void
  onHandleTrainingCopy: (athleteTraining: any) => void
}

export const AthleteTrainingDataList = ({ items, onEdit, onAthleteTrainingList, onHandleTrainingCopy }: Props) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(null)
  const [athleteTraining, setAthleteTraining] = useState<any | null>(null)
  const [indexRoutine, setIndexRoutine] = useState<any>()
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const toggleDialog = () => setOpenConfirmation(!openConfirmation)
  const mdUp = useResponsive('up', 'md')

  const handleOpenMenu = (event: any, training: any, index: number) => {
    setOpen(event.currentTarget)
    setAthleteTraining(training)
    setIndexRoutine(index)
  }

  const handleCloseMenu = () => {
    setOpen(null)
  }

  const handleGoToDetail = (item: AthleteTraining) => {
    navigate(`${item.id}/training-workouts`, { state: item })
  }

  const handleRemove = async () => {
    try {
      await api.delete(`professional-athlete-trainings/${athleteTraining?.id}`)
      dispatch(showMessage({ message: `${t('training.delete_success')}` }))
      items.splice(indexRoutine, 1)
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
    setOpenConfirmation(false)
    onAthleteTrainingList(items)
  }

  return (
    <>
      <DialogConfirmation
        title={`${t('training.deleteConfirmation_title')}`}
        message={`${t('training.deleteConfirmation_messenger', { name: athleteTraining?.name })}`}
        open={openConfirmation}
        onClose={toggleDialog}
        action={handleRemove}
      />
      {items
        .sort((athleteA, athleteB) => new Date(athleteA.createdAt).getTime() - new Date(athleteB.createdAt).getTime())
        .sort((athleteA, athleteB) => ReorderDaysOfWeek(athleteA.sequence, athleteA?.professionalAthleteRoutine?.type) - ReorderDaysOfWeek(athleteB.sequence, athleteB?.professionalAthleteRoutine?.type))
        .map((item: any, index: any) => (
          <Card key={index} sx={{ pl: '10px', mb: '10px' }}>
            <Grid padding={1} key={`container_${index}`} container alignItems="center">
              <Grid item xs={10} md={10.7} sx={{ cursor: 'pointer' }} onClick={() => { handleGoToDetail(item) }}>
                <Stack direction="row" spacing={1} padding={0.5} alignItems="center" >
                  <Stack justifyContent="center">
                    <Avatar sx={{ width: '60px', height: '60px', bgcolor: theme.palette.primary.main }}>
                      <Iconify color="#FFF" icon="healthicons:exercise-outline" width="40px"></Iconify>
                    </Avatar>
                  </Stack>
                  <Stack spacing={1} padding={1}>
                    <Stack direction="row" spacing={1}>
                      <SequenceTypeIcon type={item.type} />
                      <Typography variant="subtitle1" textAlign={'justify'}>
                        {getTitleByType(Number(item.sequence), item?.professionalAthleteRoutine?.type)?.label} - {item.name}
                      </Typography>
                    </Stack>
                    <Tooltip arrow placement='top' title={`${item.note}`}>
                      <Stack sx={{ textAlign: 'justify' }} marginTop={1} spacing={0.5}>
                        <Typography variant="body1" textAlign={'justify'}>
                          { setLimitCharacter(item.note, (mdUp ? 448 : 68)) }
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Grid >
              <Grid item xs={2} md={1.3} sx={{ cursor: 'normal' }} textAlign={'center'}>
                <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, item, index)}>
                  <Iconify icon={'eva:more-vertical-fill'} />
                </IconButton>
              </Grid>
            </Grid>
          </Card>
        ))
      }
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75
            }
          }
        }}
      >
        <MenuItem onClick={() => {
          handleCloseMenu()
          onHandleTrainingCopy(athleteTraining)
        }}>
          <Iconify icon={'eva:copy-fill'} sx={{ mr: 2 }}/>
          {t('training.copyTo')}
        </MenuItem>

        <MenuItem onClick={() => {
          handleCloseMenu()
          onEdit(athleteTraining)
        }}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          {t('general.btn.edit')}
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={() => {
          handleCloseMenu()
          toggleDialog()
        }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          {t('general.btn.delete')}
        </MenuItem>
      </Popover>
    </>
  )
}
